import { ticks } from './config';
import { ROYALE_MAP_ID } from './royale';
import { Team } from './teams';
import {
  ALIEN_ATTACK,
  type AssistLevel,
  ATOM_BOMB,
  BERSERKER,
  BUZZSAW,
  CORN_RAIL,
  DUBSTEP,
  FIREFLY,
  FIREWORK,
  FROSTBITE,
  HAMMER,
  LONGSTEM,
  MECHA,
  MONSTER,
  POISON_DART,
  POLLINATOR,
  PRUNER,
  ROOT_ROCKET,
  SHIELD,
  SHOTGUN,
  SHURIKEN,
  SLUDGE,
  SUNBEAM,
  TANK,
  THORNFIRE,
  UNICORN,
  WAND,
  WEED_BURNER,
  WHACKER,
} from './weapons';
import { ZOMBIE_MAP_ID } from './zombies';

/**
 * What kind of game a match is (ADR-0021): the shooting range, Zombie Nights (ADR-0098) or the battle royale
 * (ADR-0120). Classic, its tutorial and the duel lab are gone (ADR-0209). The mode picks the map and switches the rules
 * below on or off; the range's altars, the zombies' nights and the Frost are sim systems of their own.
 */
export type GameMode = 'range' | 'zombies' | 'royale';

export interface ModeRules {
  /** The map it is played on. */
  readonly map: string;
  /** Jungle camps growing (and growing back) on their own timers. */
  readonly camps: boolean;
  /** After the Greenhouse falls the next run follows an intermission; otherwise the match is over. */
  readonly rounds: boolean;
  /** Everyone plays on this team (null: teams as usual). */
  readonly team: number | null;
  /** How much humans' shots are helped (ADR-0026). */
  readonly assist: AssistLevel;
  /** A fallen player is back this many ticks later (null: the gardener's own time, Zombie Nights' by its day). */
  readonly respawn: number | null;
  /**
   * The jungle is a zoo (the shooting range, ADR-0100): every den keeps its own boss (den i: BossKind i) and every camp
   * its own creature, all up at once, never retreating, growing back `RANGE.bossRegrowTicks` / `RANGE.campRegrowTicks`
   * after they fall.
   */
  readonly zoo: boolean;
  /**
   * The last team standing wins (a battle royale, ADR-0120): a fallen player stays down for the rest of the round, and a
   * team with nobody standing is out of it.
   */
  readonly lastStanding: boolean;
  /** A gun taken is carried for good (no 30 s clock, ADR-0025) and a dropped one lies where it fell for good. */
  readonly keepGuns: boolean;
  /** Crates on their spots (apple crates, chests) grow back after they break; otherwise once a round. */
  readonly regrow: boolean;
  /** Strike guns (the Atom Bomb, the Alien Attack) come out of crates, one a round (ADR-0080). */
  readonly strikes: boolean;
}

export const MODES: Record<GameMode, ModeRules> = {
  // Blue, not neutral: your own blasts (the atom bomb) spare you, and the neutral dummies are fair game. Its jungle is
  // a zoo of every creature and boss (ADR-0100): they can kill you, and you are back at the cottage a moment later.
  range: {
    map: 'range',
    camps: true,
    rounds: false,
    team: Team.Blue,
    assist: 'standard',
    respawn: ticks(1),
    zoo: true,
    lastStanding: false,
    keepGuns: false,
    regrow: true,
    strikes: true,
  },
  // Zombie Nights (ADR-0098): the party (blue) against the nights on Moonlit Farm; its clock is `nightSystem`'s. A run
  // is a round: when the Greenhouse falls the next run follows an intermission. Aim help as against the bots.
  zombies: {
    map: ZOMBIE_MAP_ID,
    camps: false,
    rounds: true,
    team: Team.Blue,
    assist: 'extra',
    respawn: null,
    zoo: false,
    lastStanding: false,
    keepGuns: false,
    regrow: true,
    strikes: true,
  },
  // The battle royale (ADR-0120): a hundred gardeners on Village (ADR-0190; trios ninety-nine), squads of one to five; the last standing wins and the room
  // closes after its one round. Nobody comes back, nothing to buy: guns are found in chests and kept, the chests never
  // grow back, and no strike gun wipes out a whole house. Its creature camps grow (`ROYALE.camps`): their prizes heal,
  // shield, speed you up or light the fog.
  royale: {
    map: ROYALE_MAP_ID,
    camps: true,
    rounds: false,
    team: null,
    assist: 'standard',
    respawn: null,
    zoo: false,
    lastStanding: true,
    keepGuns: true,
    regrow: false,
    strikes: false,
  },
};

// ─── Shooting range ───────────────────────────────────────────────────────────────────────────────────

/** The shooting range (ADR-0021, a cottage garden since ADR-0100). */
export const RANGE = {
  /**
   * The sparring pen (`m`, ADR-0035): red minions as tough as a gardener (`hp`) that never fight back — sandbags that
   * can be pulled, punched and thrown, and walk back to their spot — and grow back `regrowTicks` after they fall.
   */
  sparring: { hp: 100, regrowTicks: ticks(3) },
  /**
   * The shooting gallery (`>`, ADR-0100): a target sliding `span` px either way of its spot along a row of water at an
   * even pace, there and back in `periodTicks` (it stops while frozen, and while broken).
   */
  slider: { span: 72, periodTicks: ticks(5) },
  /** The zoo (ADR-0100): the bosses wake this long in and this long after one falls; the camps grow back this soon. */
  bossFirstTicks: ticks(2),
  bossRegrowTicks: ticks(25),
  campFirstTicks: ticks(1),
  campRegrowTicks: ticks(15),
  /**
   * The abandoned garden's graves (`z`, ADR-0101): each keeps a zombie of its kind, at night 1's strength, climbing out
   * `riseTicks` after the last one fell (the first ones at once). They come for a gardener within `aggro` px (their
   * kind's own when farther) and walk back to their grave once nobody is about.
   */
  graves: { riseTicks: ticks(6), aggro: 120 },
  /** Every gun the range's armoury hands out (ADR-0100: its window, in this order): guns, launchers and tricks, the rest. */
  guns: [
    // Guns.
    POLLINATOR.id,
    THORNFIRE.id,
    LONGSTEM.id,
    WHACKER.id,
    SHOTGUN.id,
    SUNBEAM.id,
    WEED_BURNER.id,
    FROSTBITE.id,
    POISON_DART.id,
    // Launchers and tricks.
    ROOT_ROCKET.id,
    FIREWORK.id,
    SLUDGE.id,
    FIREFLY.id,
    CORN_RAIL.id,
    DUBSTEP.id,
    // ADR-0099: in the archived Cupid's Bow's place.
    ALIEN_ATTACK.id,
    // Blades, fists and the wild ones.
    PRUNER.id,
    BERSERKER.id,
    BUZZSAW.id,
    SHURIKEN.id,
    ATOM_BOMB.id,
    MECHA.id,
    WAND.id,
    MONSTER.id,
    SHIELD.id,
    // ADR-0159.
    HAMMER.id,
    TANK.id,
    UNICORN.id,
  ],
} as const;
